import React, { useState, useMemo, useRef } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { format, subDays } from 'date-fns';

import { useRatesCurve } from '../api/treasuryApi';
import { formatDate, formatYield } from '../utils/formatters';
import { ChartDownloadMenu } from '../../../../shared/ui';
import { useChartExport } from '../../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../../shared/utils/chartFilename';

// Map the physical backend columns to display labels in chronological maturity order
const TENOR_MAPPING = [
    { key: 'month1', label: '1M' },
    { key: 'month2', label: '2M' },
    { key: 'month3', label: '3M' },
    { key: 'month6', label: '6M' },
    { key: 'year1',  label: '1Y' },
    { key: 'year2',  label: '2Y' },
    { key: 'year3',  label: '3Y' },
    { key: 'year5',  label: '5Y' },
    { key: 'year7',  label: '7Y' },
    { key: 'year10', label: '10Y' },
    { key: 'year20', label: '20Y' },
    { key: 'year30', label: '30Y' },
];

export const HistoricalCurveExplorer: React.FC = () => {
    // State to hold the user's selected date string (YYYY-MM-DD).
    // Start empty so the backend automatically fetches the absolute latest available day.
    const [selectedDate, setSelectedDate] = useState<string>('');

    // Fetch the single date from the highly optimized /rates/curve endpoint
    const { data, isLoading, error } = useRatesCurve(
        selectedDate !== '' ? selectedDate : undefined
    );

    const containerRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(containerRef);

    // Recharts requires an array of objects to draw an AreaChart.
    // We take the single backend object and pivot it into an array of { tenorLabel: '1M', yield: 5.4 } objects.
    const chartData = useMemo(() => {
        if (!data) return [];
        return TENOR_MAPPING.map((mapping) => ({
            tenorLabel: mapping.label,
            // Cast as 'any' to dynamically index the object safely
            yield: (data as any)[mapping.key] as number | null | undefined,
        }));
    }, [data]);

    // Fast-jump date buttons
    const handleJumpToDate = (daysAgo: number) => {
        if (daysAgo === 0) {
            setSelectedDate(''); // Reset to latest
        } else {
            setSelectedDate(format(subDays(new Date(), daysAgo), 'yyyy-MM-dd'));
        }
    };

    // Custom Tooltip
    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null;

        const point = payload[0];

        // Don't render the tooltip if the user is hovering over a missing data point (null)
        if (point.value === null || point.value === undefined) return null;

        return (
            <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-4 py-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 z-50">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                    {point.payload.tenorLabel} Treasury
                </p>
                <p className="font-mono text-xl font-bold text-emerald-600 dark:text-emerald-400">
                    {formatYield(point.value)}
                </p>
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 flex flex-col h-full min-h-[450px]">

            {/* Header & Controls */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        Historical Curve Shape
                        {isLoading && (
                            <span className="flex h-2 w-2 relative ml-1">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                        )}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1 max-w-sm">
                        Select a date to visualize the exact structure of the yield curve.
                    </p>
                </div>

                <div className="flex flex-col items-end gap-2">
                    {/* Native Browser Date Picker */}
                    <input
                        type="date"
                        max={format(new Date(), 'yyyy-MM-dd')}
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none transition-shadow"
                    />

                    {/* Quick-Jump Buttons */}
                    <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-md">
                        {[
                            { label: 'Latest', days: 0 },
                            { label: '-1M', days: 30 },
                            { label: '-1Y', days: 365 },
                            { label: '-5Y', days: 1825 }
                        ].map((btn) => (
                            <button
                                key={btn.label}
                                onClick={() => handleJumpToDate(btn.days)}
                                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700 shadow-none hover:shadow-sm transition-all"
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>

                    <ChartDownloadMenu
                        label="Historical Curve Shape chart"
                        onPng={() => downloadPng({ filename: buildChartFilename('curve-shape', [data?.date || 'latest']) })}
                        onJpeg={() => downloadJpeg({ filename: buildChartFilename('curve-shape', [data?.date || 'latest']) })}
                    />
                </div>
            </div>

            {/* Resolved Date Notice */}
            <div className="h-8 flex items-center mb-4">
                {data?.date && selectedDate && data.date !== selectedDate && !isLoading && (
                    <div className="text-xs font-semibold text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 px-3 py-1.5 rounded-md flex items-center border border-amber-200 dark:border-amber-800/50">
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        Displaying {formatDate(data.date)}.
                    </div>
                )}
            </div>

            {/* Chart Area */}
            <div ref={containerRef} className="flex-1 relative min-h-[250px]">
                {error && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center text-rose-500 bg-rose-50/50 dark:bg-rose-900/10 rounded-lg text-sm">
                        Failed to load curve data.
                    </div>
                )}

                {chartData.length > 0 && !error && (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorCurve" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={false} stroke="#cbd5e1" opacity={0.2} />

                            <XAxis
                                dataKey="tenorLabel"
                                tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }}
                                axisLine={false}
                                tickLine={false}
                                dy={10}
                            />
                            <YAxis
                                domain={['auto', 'auto']}
                                tickFormatter={(tick) => `${tick.toFixed(1)}%`}
                                tick={{ fontSize: 12, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                content={<CustomTooltip />}
                                cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '4 4' }}
                            />

                            <Area
                                type="monotone"
                                dataKey="yield"
                                stroke="#10b981"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorCurve)"
                                // Recharts elegantly connects lines if a single maturity (e.g. 2M) is missing from old history
                                connectNulls={true}
                                activeDot={{ r: 6, strokeWidth: 0, fill: '#059669' }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};
